import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  completeStaffPasswordReset,
  validateStaffPasswordReset,
} from "../api/auth";
import { formatApiError } from "../utils/formatApiError";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = (searchParams.get("token") || "").trim();
  const salonIdParam =
    (searchParams.get("salonId") || searchParams.get("salon_id") || "").trim();
  const salonId = useMemo(() => Number(salonIdParam || 0), [salonIdParam]);

  const [loading, setLoading] = useState(true);
  const [validInfo, setValidInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!token || !salonId) {
        setLoading(false);
        setValidInfo(null);
        return;
      }
      setLoading(true);
      setError("");
      try {
        const data = await validateStaffPasswordReset(token, salonId);
        if (!active) return;
        setValidInfo(data?.valid ? data : null);
      } catch (_err) {
        if (!active) return;
        setValidInfo(null);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [salonId, token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      await completeStaffPasswordReset({
        salon_id: salonId,
        token,
        password,
      });
      setSuccess(true);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Staff password reset error:", err);
      }
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const loginTarget = salonId
    ? `/login?salonId=${encodeURIComponent(String(salonId))}`
    : "/login";
  const showInvalid = !loading && !validInfo;

  return (
    <div className="min-h-screen bg-[#fdeee9] flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-start px-4 pt-3 pb-6 sm:pt-4 sm:pb-8 lg:justify-center lg:py-4">
        <div className="w-full max-w-md bg-white border border-gray-200 shadow-lg rounded-lg px-8 py-8">
          {loading && <div className="text-sm text-slate-600">Loading...</div>}

          {showInvalid && (
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-gray-900">
                This reset link is invalid or expired.
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Please request a new password reset link.
              </p>
              <div className="mt-4">
                <Link to={loginTarget} className="text-rose-600 hover:text-rose-700">
                  Back to sign in
                </Link>
              </div>
            </div>
          )}

          {!loading && validInfo && success && (
            <div className="text-center">
              <h1 className="text-2xl font-semibold text-gray-900">
                Password updated
              </h1>
              <p className="mt-2 text-sm text-slate-600">
                Sign in with your new password.
              </p>
              <div className="mt-4">
                <Link
                  to={loginTarget}
                  className="rounded bg-rose-500 hover:bg-rose-600 text-white font-semibold px-4 py-2.5 text-sm shadow-sm"
                >
                  Back to sign in
                </Link>
              </div>
            </div>
          )}

          {!loading && validInfo && !success && (
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">
                Create a new password
              </h1>
              <p className="text-sm text-slate-600">
                Choose a new password for your account.
              </p>
              {validInfo?.name && (
                <p className="mt-2 text-xs text-slate-500">
                  Account: {validInfo.name}
                </p>
              )}
              {validInfo?.email_masked && (
                <p className="text-xs text-slate-500">
                  Email on file: {validInfo.email_masked}
                </p>
              )}

              {error && (
                <div className="mt-4 p-3 rounded bg-rose-50 border border-rose-200 text-rose-700 text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                <div>
                  <label
                    htmlFor="reset-password-new"
                    className="block text-sm font-medium text-gray-700"
                  >
                    New password
                  </label>
                  <input
                    id="reset-password-new"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="reset-password-confirm"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Confirm password
                  </label>
                  <input
                    id="reset-password-confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                    className="mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-rose-300 focus:border-rose-400"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded bg-rose-500 hover:bg-rose-600 text-white font-semibold px-4 py-2.5 text-sm shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {saving ? "Saving..." : "Update password"}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
